import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Keyboard, StyleSheet, View } from 'react-native'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { debounce } from 'lodash'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { GameModeTag, TournamentModelType } from '../../models/api-store'
import { observer } from 'mobx-react-lite'
import GameCodeTextInput from './GameCodeTextInput'
import { TextInput } from 'react-native-gesture-handler'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { BottomSheetView } from '@gorhom/bottom-sheet'
import SendMessageButton from '@/components/ANewDesign/BottomModalContent/Content/Chats/partials/SendMessageButton'
import { useRouter } from 'expo-router'
import { isWeb } from '@/components/WebWrapper'
import { events } from '@/models/analytics/analytics-events'
import { translate } from '../../i18n'
import { useConvex } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { redirectToLobby } from '@/screens/tournament/mttNavigation'

const logger = extendLogger(LoggerContexts.ActiveGameScreen)

const JoinGameSheetContent = observer(({ code }: { code?: string }) => {
  const router = useRouter()
  const store = useStore()
  const convex = useConvex()
  const setGameKind = store.game.setGameKind
  const searchbarRef = useRef<TextInput>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [manualPasscode, setManualPasscode] = useState(code ?? '')
  const [loading, setLoading] = useState(false)
  const clearCurrentTournament = store.game.clearCurrentTournament
  const setCurrentTournament = store.game.setCurrentTournament
  const queryUserGameScheduleByHash = store.game.queryUserGameScheduleByHash
  const setUserGameScheduleFullResponse = store.game.setUserGameScheduleFullResponse
  const insets = useSafeAreaInsets()

  const isInvalidInput = manualPasscode.length !== 6
  const [userRole, setUserRole] = useState('player')

  useEffect(() => {
    const getScheduleTournaments = async () => {
      try {
        await store.game.getScheduledTournaments()
      } catch (error) {
        logger.error('Error in getScheduledTournaments: ', error)
      }
    }
    getScheduleTournaments()
  }, [store.game])

  const reset = () => {
    logger.debug('join reset')
    Keyboard.dismiss()
    setManualPasscode('')
    setErrorMessage(null)
    setLoading(false)
    setUserRole('player')
  }

  const submitCode = async (isTeacher: boolean) => {
    try {
      if (isInvalidInput && !store.share.info) {
        setErrorMessage(translate('join.errorInvalidCode'))
        return
      }
      store.communication.clearChannel()
      store.game.clearGameModalQueue()
      store.messages.clearMessages()
      store.communication.clearChannelChats()

      const userId = store.session.user?.auth0Id
      const userName = store.session.user?.name || 'Player'
      const mongoUserId = store.session.user?._id.toString()
      if (!userId || !mongoUserId) {
        setErrorMessage(translate('join.errorNotLoggedIn'))
        return
      }

      if (!manualPasscode) {
        setErrorMessage(translate('join.errorNoCode'))
        return
      }

      logger.debug('doSubmit code', manualPasscode)
      logger.debug('doSubmit userId', userId)

      // First, check if game exists in Convex (private quick games)
      const convexGame = await convex.query(api.games.queries.getGameByCode, { code: manualPasscode })
      if (convexGame?.canJoin) {
        logger.debug('Found Convex game:', convexGame.gameId)

        // Teachers join as observers server-side and pick their seat in-game (TeacherSeatChoiceModal).
        await convex.mutation(api.games.mutations.joinGame, {
          gameId: convexGame.gameId,
          userId,
          mongoUserId,
          name: userName
        })
        reset()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        router.replace({ pathname: '/game/[gameId]', params: { gameId: convexGame.gameId } } as any)
        return
      }

      if (convexGame && !convexGame.canJoin) {
        if (convexGame.state === 'finished') {
          setErrorMessage(translate('join.errorGameEnded'))
          logger.debug('Convex game found but cannot join:', convexGame.state, convexGame.playerCount)
          return
        }
        const activeGame = convexGame.state === 'pregame' || convexGame.state === 'playing'
        if (isTeacher && activeGame) {
          // Full table: teachers still get in as observers (joinGame redirects them before the seat check).
          await convex.mutation(api.games.mutations.joinGame, {
            gameId: convexGame.gameId,
            userId,
            mongoUserId,
            name: userName
          })
          reset()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          router.replace({ pathname: '/game/[gameId]', params: { gameId: convexGame.gameId } } as any)
          return
        }
        setErrorMessage(translate('join.errorGameFull'))
        logger.debug('Convex game found but cannot join:', convexGame.state, convexGame.playerCount)
        return
      }

      // Check for Convex MTT by code
      const convexTournament = await convex.query(api.tournaments.queries.getTournamentByCode, {
        code: manualPasscode
      })
      if (convexTournament) {
        logger.debug('Found Convex tournament:', convexTournament._id)
        reset()
        redirectToLobby(router, convexTournament._id)
        return
      }

      // Check for Convex scheduled tournament by hash
      const convexSchedule = await convex.query(api.gameSchedules.queries.getScheduleByHash, { hash: manualPasscode })
      if (convexSchedule && !('restricted' in convexSchedule)) {
        logger.debug('Found Convex scheduled tournament:', convexSchedule._id)
        reset()
        if (convexSchedule.hostId === userId) {
          // Host goes to confirmation screen
          router.replace(`/game/create/scheduled-tournament-confirm?hash=${manualPasscode}`)
        } else {
          // Others go to accept invite screen
          router.replace(`/game/join/accept-scheduled-invite/${manualPasscode}`)
        }
        return
      }

      // Fall back to MongoDB lookup for legacy games
      const game = await store.game.getGameByHash(manualPasscode.toLocaleLowerCase())
      const channelName = game?.channelName
      setGameKind(game?.kind as GameModeTag)

      logger.debug('doSubmit channelName', channelName)
      if (channelName) {
        if (isTeacher) {
          reset()
          router.replace(`/game/create/user-role?code=${manualPasscode}`)
          return
        }
        store.communication.joinChannel(channelName)
        if (store.game.isTournamentModeOn) {
          clearCurrentTournament()
          setCurrentTournament(game.tournament as TournamentModelType)
        }
        //Todo: Check the attributes and update. Remove ts-ignore
        //@ts-ignore
        await store.telemetry.setUser({ ...store.session.user, gameId: store.game.gameId })
        reset()
        router.replace('/game')
      } else {
        setUserGameScheduleFullResponse(null)
        const userGameSchedule = await queryUserGameScheduleByHash(manualPasscode)

        if (userGameSchedule.isHost) {
          router.replace('/game/create/schedule-confirm')
        } else {
          router.replace(`/game/join/accept-invite?code=${userGameSchedule.hash}`)
        }
      }
    } catch (error) {
      setErrorMessage(translate('join.errorGameNotFound'))
      logger.error('doSubmit error', error)
    }
  }

  const onSubmitCode = debounce(submitCode, 250)

  const onJoinGame = () => {
    if (isInvalidInput && !store.share.info) {
      setErrorMessage(translate('join.errorInvalidCode'))
      return
    }
    store.game.setPlayKind(userRole)
    onSubmitCode(store.session.isTeacher)
  }

  // const navToLandingScreen = useCallback(
  //   (gameMode: GameModeTag) => {
  //     const event =
  //       gameMode === 'default'
  //         ? events.joinLiveScreen.join_quick_game_tapped
  //         : events.joinLiveScreen.join_open_tournament_tapped
  //     store.analytics.setEvent(event)
  //     store.communication.clearChannel()
  //     store.game.clearCurrentTournament()
  //     router.replace(`/game/join/open-table/${gameMode}`)
  //   },
  //   [store.communication, store.game]
  // )

  const navToLandingScreen = useCallback(
    async (gameMode: GameModeTag) => {
      const event =
        gameMode === 'default'
          ? events.joinLiveScreen.join_quick_game_tapped
          : events.joinLiveScreen.join_open_tournament_tapped
      store.analytics.setEvent(event)
      store.communication.clearChannel()
      store.game.clearCurrentTournament()

      const userId = store.session.user?.auth0Id
      const userName = store.session.user?.name || 'Player'
      const mongoUserId = store.session.user?._id.toString()

      if (!userId || !mongoUserId) {
        setErrorMessage(translate('join.errorNotLoggedIn'))
        return
      }

      try {
        setLoading(true)
        const result = await convex.mutation(api.games.mutations.joinOpenTableGame, {
          userId,
          mongoUserId,
          name: userName,
          shouldBlindsIncrease: gameMode === GameModeTag.tournament
        })

        reset()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        router.replace({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as any)
      } catch (error) {
        logger.error('Failed to create Convex open game:', error)
        setErrorMessage(translate('join.errorGameNotFound'))
        setLoading(false)
      }
    },
    [store.communication, store.game, store.session.user, convex, router]
  )

  return (
    <BottomSheetView style={[styles.container, { paddingBottom: 32 }]}>
      <DS.Text variant="Heading 2/Medium">{translate('join.joinOpenGame')}</DS.Text>
      <DS.Frame row gap={'8'}>
        <DS.Button
          maxWidth
          variant="Large/Primary"
          type="On Light"
          onPress={() => navToLandingScreen(GameModeTag.default)}
        >
          Quick Game
        </DS.Button>
        <DS.Button
          maxWidth
          variant="Large/Primary"
          type="On Light"
          onPress={() => navToLandingScreen(GameModeTag.tournament)}
        >
          Tournament
        </DS.Button>
      </DS.Frame>
      <DS.Frame style={{ width: '100%', height: 1, backgroundColor: DS.palette['Brand/Light Grey'] }} />
      <DS.Text variant="Heading 5/Medium">Private Game</DS.Text>

      <DS.Frame row>
        <GameCodeTextInput
          textInputRef={searchbarRef}
          placeholder="Enter game code"
          allowFontScaling={false}
          autoCapitalize={'none'}
          autoCorrect={false}
          autoFocus={!isWeb}
          maxLength={6}
          onChangeText={(value) => {
            setErrorMessage(null)
            setManualPasscode(value.toUpperCase())
          }}
          onSubmitEditing={onJoinGame}
          returnKeyType="go"
          value={manualPasscode}
          placeholderTextColor={'#00000050'}
          editable={!loading}
          style={styles.inputCodeStyle}
        />
        <View
          style={{
            position: 'absolute',
            top: 8,
            right: 12
          }}
        >
          <SendMessageButton onPress={onJoinGame} disabled={manualPasscode === ''} />
        </View>
      </DS.Frame>
      <View style={{ paddingBottom: insets.bottom }}>
        {errorMessage && (
          <DS.Text variant="Body Small/Medium" color="Utility/Warning" textAlign="center">
            {errorMessage}
          </DS.Text>
        )}
      </View>
    </BottomSheetView>
  )
})

export default JoinGameSheetContent

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    paddingHorizontal: 32,
    gap: 16,
    alignItems: 'center',
    backgroundColor: DS.palette['Brand/Cream']
  },
  inputCodeStyle: {
    fontWeight: '400',
    color: '#000000'
  }
})
